import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star,
  MapPin,
  Camera,
  Share2,
  CheckCircle,
  Zap,
  Crown,
  X,
  ArrowLeft,
} from "lucide-react";
import {
  OverviewTab,
  ServicesTab,
  ReviewsTab,
  ContactTab,
} from "../../components/client/service";
import { useVendorService } from "../../services/vendorService";
import ServiceDetailSkeleton from "../../components/shared/ServiceDetailSkeleton";
import { useAuth } from "../../contexts/AuthContext";
import useShare from "../../hooks/useShare";
import BookingWidget from "../../components/client/BookingWidget";
import MarketingReveal from "../../components/home/MarketingReveal";

const ClientVendorServices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [showMobileBooking, setShowMobileBooking] = useState(false);

  const { data: serviceResponse, isLoading, error } = useVendorService(id);

  const service = serviceResponse?.data;
  const vendor = service?.vendor_profile;
  const category = service?.service_category;

  const serviceRating = service?.average_rating || 0;
  const serviceReviews = service?.total_reviews || 0;
  const serviceRatingStats = service?.rating_stats || {
    total_reviews: 0,
    average_rating: 0,
    five_star: 0,
    four_star: 0,
    three_star: 0,
    two_star: 0,
    one_star: 0,
  };

  const hasPackages = service?.vendor_service_packages?.length > 0;

  const calculateStartingPrice = () => {
    if (hasPackages && service?.vendor_service_packages?.length > 0) {
      const prices = service.vendor_service_packages
        .map((pkg) => parseFloat(pkg.base_price || 0))
        .filter((price) => price > 0);

      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }
    return parseFloat(service?.base_price || 0);
  };

  const startingPrice = calculateStartingPrice();

  const formatPrice = (price) => {
    if (!price || price === 0) return "Contact";
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const { handleShare } = useShare({
    title: service?.service_name || "Service",
    text: `Check out ${service?.service_name}${
      vendor?.business_name ? ` by ${vendor.business_name}` : ""
    } on VendaList`,
  });

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "services", label: "Services & Pricing" },
    { id: "reviews", label: "Reviews" },
    { id: "contact", label: "Contact" },
  ];

  if (isLoading) {
    return <ServiceDetailSkeleton />;
  }

  if (error || !service) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-m-canvas-light px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-m-border-light bg-m-surface">
            <Camera className="h-7 w-7 text-m-ink-muted" />
          </div>
          <h2 className="mb-3 text-2xl font-medium tracking-tight text-m-ink">
            Service not found
          </h2>
          <p className="marketing-body mb-8 text-m-ink-muted">
            This service doesn’t exist or is no longer available.
          </p>
          <Link to="/browse-vendors" className="marketing-btn marketing-btn-ink gap-2">
            <ArrowLeft className="h-4 w-4" />
            Browse vendors
          </Link>
        </div>
      </div>
    );
  }

  const heroImage =
    service?.image_url || vendor?.image_url || vendor?.user?.profile_image_url;

  return (
    <div className="min-h-screen bg-m-canvas-light pb-24 lg:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="mb-5 lg:hidden">
          <Link
            to={vendor?.id ? `/vendor/${vendor.id}` : "/browse-vendors"}
            className="inline-flex items-center gap-2 text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>

        <MarketingReveal>
          <h1 className="marketing-display-md mb-6 text-m-ink">
            {service?.service_name}
          </h1>
        </MarketingReveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_400px] lg:gap-12">
          <div className="space-y-8">
            <MarketingReveal>
              <div className="relative">
                <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-m-border-light sm:aspect-[16/10]">
                  {heroImage ? (
                    <img
                      src={heroImage}
                      alt={service?.service_name || "Service"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="text-center">
                        <Camera className="mx-auto mb-3 h-12 w-12 text-m-ink-faint" />
                        <p className="text-sm text-m-ink-muted">Service preview</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="absolute left-4 top-4 flex flex-col gap-2">
                  <span className="inline-flex items-center rounded-full border border-white/30 bg-white/90 px-3 py-1.5 text-xs font-medium text-m-ink backdrop-blur-sm">
                    {category?.category_name || "Service"}
                  </span>
                  {service?.is_featured ? (
                    <span className="inline-flex items-center rounded-full bg-m-ink px-3 py-1.5 text-xs font-medium text-white">
                      <Crown className="mr-1 h-3 w-3" />
                      Featured
                    </span>
                  ) : null}
                </div>
              </div>
            </MarketingReveal>

            <div className="border-b border-m-border-light pb-6">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {vendor?.is_verified ? (
                  <div className="inline-flex items-center rounded-full border border-m-border-light bg-m-surface px-2.5 py-1 text-m-ink">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    <span className="text-xs font-medium">Verified vendor</span>
                  </div>
                ) : null}
                <div className="inline-flex items-center text-m-ink-muted">
                  <MapPin className="mr-1 h-3 w-3" />
                  <span className="text-xs">
                    {vendor
                      ? `${vendor.city}, ${vendor.state}`
                      : "Location not available"}
                  </span>
                </div>
              </div>

              <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFilled = star <= Math.floor(serviceRating);
                      const hasPartial =
                        star === Math.ceil(serviceRating) &&
                        serviceRating % 1 !== 0;
                      return (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            isFilled || hasPartial
                              ? "fill-current text-m-ink"
                              : "text-m-border-light"
                          } ${hasPartial ? "opacity-50" : ""}`}
                        />
                      );
                    })}
                  </div>
                  <span className="ml-1 text-sm font-medium text-m-ink">
                    {serviceRating.toFixed(1)}
                  </span>
                  <span className="ml-1 text-sm text-m-ink-muted">
                    ({serviceReviews} reviews)
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-m-ink underline underline-offset-2 transition-opacity hover:opacity-70"
                  aria-label="Share service"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              </div>

              <p className="text-sm text-m-ink-muted sm:text-base">
                by{" "}
                {vendor?.id ? (
                  <Link
                    to={`/vendor/${vendor.id}`}
                    className="font-medium text-m-ink underline underline-offset-2 hover:opacity-70"
                  >
                    {vendor?.business_name}
                  </Link>
                ) : (
                  <span className="font-medium text-m-ink">
                    {vendor?.business_name}
                  </span>
                )}
              </p>
            </div>

            <div className="border-b border-m-border-light pb-6">
              <p className="marketing-body whitespace-pre-line text-m-ink-muted">
                {service?.description ||
                  "Experience exceptional service with our professional team. We deliver quality and excellence in every project."}
              </p>
            </div>

            <div className="mb-6 border-b border-m-border-light">
              <nav className="-mb-px flex overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative whitespace-nowrap border-b-2 px-4 py-4 text-sm font-medium transition-colors sm:px-6 sm:text-base ${
                      activeTab === tab.id
                        ? "border-m-ink text-m-ink"
                        : "border-transparent text-m-ink-muted hover:border-m-border-light hover:text-m-ink"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div>
              {activeTab === "overview" && (
                <OverviewTab
                  service={service}
                  vendor={vendor}
                  serviceRating={serviceRating}
                  serviceReviews={serviceReviews}
                  formatPrice={formatPrice}
                  startingPrice={startingPrice}
                />
              )}

              {activeTab === "services" && <ServicesTab service={service} />}

              {activeTab === "reviews" && (
                <ReviewsTab
                  serviceRating={serviceRating}
                  serviceReviews={serviceReviews}
                  serviceRatingStats={serviceRatingStats}
                  vendorId={vendor?.id || vendor?.user_id}
                />
              )}

              {activeTab === "contact" && <ContactTab />}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-20">
              <BookingWidget
                service={service}
                vendor={vendor}
                category={category}
                startingPrice={startingPrice}
                hasPackages={hasPackages}
                serviceRating={serviceRating}
                serviceReviews={serviceReviews}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
                formatPrice={formatPrice}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-m-border-light bg-m-surface/95 shadow-[0_-8px_30px_rgba(0,0,0,0.06)] backdrop-blur-sm lg:hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-medium tracking-tight text-m-ink">
                ${formatPrice(startingPrice)}
              </span>
              <span className="text-sm text-m-ink-muted">starting from</span>
            </div>
            <button
              type="button"
              onClick={() => setShowMobileBooking(true)}
              className="marketing-btn marketing-btn-ink gap-2 text-sm"
            >
              <Zap className="h-4 w-4" />
              Book
            </button>
          </div>
        </div>
      </div>

      {showMobileBooking ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileBooking(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[90vh] overflow-y-auto rounded-t-2xl bg-m-surface shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-m-border-light bg-m-surface px-4 py-3">
              <h3 className="text-lg font-medium tracking-tight text-m-ink">
                Booking details
              </h3>
              <button
                type="button"
                onClick={() => setShowMobileBooking(false)}
                className="rounded-full p-2 transition-colors hover:bg-m-canvas-light"
              >
                <X className="h-5 w-5 text-m-ink-muted" />
              </button>
            </div>
            <div className="p-4">
              <BookingWidget
                service={service}
                vendor={vendor}
                category={category}
                startingPrice={startingPrice}
                hasPackages={hasPackages}
                serviceRating={serviceRating}
                serviceReviews={serviceReviews}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
                formatPrice={formatPrice}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClientVendorServices;
